import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faQuestionCircle,
  faSun, faMoon, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import {
  productsAPI, reviewsAPI,
  followAPI, wishlistAPI, questionsAPI, recommendationsAPI,
  priceAlertsAPI, ordersAPI,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getStoredDarkMode, setStoredDarkMode } from '../utils/theme';
import { useNotifications } from '../context/NotificationContext';
import iphoneImage from '../assets/images/mockups/iphone15promax-portrait.png';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import ProductGallery from './ProductGallery';
import ProductActions from './ProductActions';
import SellerInsight from './SellerInsight';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import './ProductDetails.css';

/* ── Tab-pane transition ─────────────────────────────────────────── */
const tabVariants = {
  initial: { opacity: 0, y: 14, filter: 'blur(4px)' },
  animate: {
    opacity: 1, y: 0, filter: 'blur(0px)',
    transition: { duration: 0.38, ease: [0.4, 0, 0.2, 1] },
  },
  exit: {
    opacity: 0, y: -10, filter: 'blur(3px)',
    transition: { duration: 0.22 },
  },
};

/* ════════════════════════════════════════════════════════════════════
   ProductDetails — clean orchestrator
═══════════════════════════════════════════════════════════════════════ */
const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showCustomToast } = useNotifications();
  const { user, isAuthenticated } = useAuth();
  const { addToCart: addToCartContext } = useCart();

  /* ── core product state ───────────────────────────────────────── */
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);

  /* ── follow / UI state ───────────────────────────────────────── */
  const [isFollowing, setIsFollowing] = useState(false);
  const [checkingFollowStatus, setCheckingFollowStatus] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  /* ── cart / wishlist ─────────────────────────────────────────── */
  const [addingToCart, setAddingToCart] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  /* ── theme ───────────────────────────────────────────────────── */
  const [isDarkMode, setIsDarkMode] = useState(() => getStoredDarkMode());

  /* ── dominant color from gallery ─────────────────────────────── */
  const [dominantRGB, setDominantRGB] = useState('34, 197, 94');

  /* ── reviews ─────────────────────────────────────────────────── */
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [ratingStats, setRatingStats] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [eligibleOrders, setEligibleOrders] = useState([]);

  /* ── Q&A ─────────────────────────────────────────────────────── */
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  /* ── price alert ─────────────────────────────────────────────── */
  const [hasPriceAlert, setHasPriceAlert] = useState(false);
  const [settingPriceAlert, setSettingPriceAlert] = useState(false);

  /* ── lightbox ────────────────────────────────────────────────── */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isSeller = user?.user_type === 'seller' || user?.userType === 'seller';
  const isReseller = user?.is_reseller || user?.isReseller;

  /* ════════════════════════════════════════════════════════════════
     Effects
  ═══════════════════════════════════════════════════════════════════ */

  // Fetch product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await productsAPI.getById(id, isSeller ? { include_seller_price: true } : {});
        if (response && response.data) {
          const data = response.data.product || response.data;
          setProduct(data);
          setFollowerCount(data.seller_followers || 0);
          if (data.variants?.length > 0) setSelectedVariant(data.variants[0]);
          if (data.category_id) {
            try {
              const rel = await productsAPI.getByCategory(data.category_id, { limit: 4 });
              const items = rel?.data?.products || rel?.data || [];
              setRelatedProducts(items.filter(p => (p.id || p._id) !== id));
            } catch {}
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        setError(err.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id, isSeller]);


  // Dark mode
  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    setStoredDarkMode(isDarkMode);
  }, [isDarkMode]);

  // Similar products
  useEffect(() => {
    if (!id) return;
    const fetchSimilar = async () => {
      try {
        const res = await recommendationsAPI.getSimilar(id, 4);
        const items = res?.data?.products || res?.data || [];
        setSimilarProducts(items.filter(p => (p.id || p._id) !== id));
      } catch {
        setSimilarProducts(relatedProducts);
      }
    };
    fetchSimilar();
  }, [id]);

  // Q&A lazy load
  useEffect(() => {
    if (activeTab !== 'qa' || !id) return;
    const fetchQ = async () => {
      setQuestionsLoading(true);
      try {
        const res = await questionsAPI.getProductQuestions(id);
        setQuestions(res?.data?.questions || res?.data || []);
      } catch {
        setQuestions([]);
      } finally {
        setQuestionsLoading(false);
      }
    };
    fetchQ();
  }, [activeTab, id]);

  // Reviews lazy load
  useEffect(() => {
    if (activeTab !== 'reviews' || !id) return;
    const fetchReviews = async () => {
      setReviewsLoading(true);
      try {
        const [rr, sr] = await Promise.all([
          reviewsAPI.getProductReviews(id, reviewsPage, 10),
          reviewsAPI.getProductRatingStats(id),
        ]);
        if (rr?.data) setReviews(rr.data.reviews || []);
        if (sr?.data) setRatingStats(sr.data);
      } catch {}
      finally { setReviewsLoading(false); }
    };
    fetchReviews();
  }, [activeTab, id, reviewsPage]);

  // Eligible orders for review form
  useEffect(() => {
    if (!showReviewForm || !isAuthenticated || !id) return;
    const fetchOrders = async () => {
      try {
        const res = await ordersAPI.getUserOrders();
        const orders = Array.isArray(res?.data) ? res.data : res?.data?.orders || [];
        setEligibleOrders(orders.filter(o => {
          const hasProduct = (o.items || []).some(item =>
            item.product_id === id || item.product?._id === id || item.product?.id === id
          );
          return hasProduct && (o.status === 'delivered' || o.status === 'completed');
        }));
      } catch { setEligibleOrders([]); }
    };
    fetchOrders();
  }, [showReviewForm, isAuthenticated, id]);

  // Wishlist status
  useEffect(() => {
    if (!product) return;
    if (!localStorage.getItem('authToken')) return;
    const check = async () => {
      try {
        const res = await wishlistAPI.getWishlist();
        const items = res?.data?.items || res?.data || [];
        const pid = product._id || product.id;
        setIsFavorite(items.some(item =>
          (item.product_id || item.product?._id || item.product?.id) === pid
        ));
      } catch {}
    };
    check();
  }, [product]);

  // Follow status + accurate follower count
  useEffect(() => {
    if (!product?.seller_id) return;

    // Fetch live follower count (public, no auth needed)
    const fetchFollowerCount = async () => {
      try {
        const res = await followAPI.getSellerProfile(product.seller_id);
        const count = res?.data?.total_followers ?? res?.data?.data?.total_followers;
        if (typeof count === 'number') setFollowerCount(count);
      } catch {}
    };
    fetchFollowerCount();

    // Check follow status only if logged in
    if (!localStorage.getItem('authToken')) return;
    const checkFollow = async () => {
      try {
        setCheckingFollowStatus(true);
        const res = await followAPI.isFollowing(product.seller_id);
        setIsFollowing(res?.data?.is_following ?? false);
      } catch {}
      finally { setCheckingFollowStatus(false); }
    };
    checkFollow();
  }, [product?.seller_id]);

  /* ════════════════════════════════════════════════════════════════
     Handlers
  ═══════════════════════════════════════════════════════════════════ */

  const handleAddToCart = async () => {
    if (!product || addingToCart) return;

    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Sign Up Required', message: 'Create an account to add items to your cart.' });
      navigate('/auth');
      return;
    }

    // Reseller category restriction
    if (isReseller) {
      const restrictedConditions = ['nigerian_used', 'nigeria_used', 'for_sale', 'want_to_buy'];
      if (restrictedConditions.includes(product.condition)) {
        showCustomToast({
          type: 'warning',
          title: 'Purchase Restriction',
          message: 'Resellers are not permitted to purchase from this category.'
        });
        return;
      }
    }

    try {
      setAddingToCart(true);
      // Go through CartContext (not cartAPI directly) so the Navbar's cart
      // badge — which reads cartCount from this same context — updates
      // immediately instead of staying stale until something else happens
      // to refetch the cart.
      await addToCartContext(product._id || product.id, 1);
      showCustomToast({ type: 'cart', title: 'Added to Cart!', message: `${product.title || product.name} added.` });
    } catch {
      showCustomToast({ type: 'error', title: 'Failed to Add', message: 'Could not add to cart.' });
    } finally { setAddingToCart(false); }
  };

  const handleSubmitQuestion = async () => {
    if (!newQuestion.trim() || submittingQuestion) return;
    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Login Required', message: 'Please login to ask.' }); return;
    }
    try {
      setSubmittingQuestion(true);
      const res = await questionsAPI.createQuestion(id, newQuestion.trim());
      if (res?.data) setQuestions(prev => [res.data.question || res.data, ...prev]);
      setNewQuestion(''); setShowQuestionForm(false);
      showCustomToast({ type: 'success', title: 'Question Submitted', message: 'Your question has been posted.' });
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Failed', message: err.message || 'Could not submit.' });
    } finally { setSubmittingQuestion(false); }
  };

  const handleTogglePriceAlert = async () => {
    if (!isAuthenticated) {
      showCustomToast({ type: 'warning', title: 'Login Required', message: 'Please login.' }); return;
    }
    if (settingPriceAlert) return;
    try {
      setSettingPriceAlert(true);
      if (hasPriceAlert) {
        setHasPriceAlert(false);
        showCustomToast({ type: 'info', title: 'Alert Removed', message: 'Price alert removed.' });
      } else {
        await priceAlertsAPI.createPriceAlert(product._id || product.id, getCurrentPrice());
        setHasPriceAlert(true);
        showCustomToast({ type: 'success', title: 'Price Alert Set!', message: `We'll notify you if the price drops.` });
      }
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Error', message: err.message || 'Could not set alert.' });
    } finally { setSettingPriceAlert(false); }
  };

  const toggleFollow = async () => {
    if (!localStorage.getItem('authToken')) {
      showCustomToast({ type: 'warning', title: 'Login Required', message: 'Please login to follow.' });
      navigate('/login'); return;
    }
    if (!product?.seller_id) {
      showCustomToast({ type: 'error', title: 'Error', message: 'Seller info not available.' }); return;
    }
    if (checkingFollowStatus) return;
    try {
      setCheckingFollowStatus(true);
      if (isFollowing) {
        await followAPI.unfollowUser(product.seller_id);
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
        showCustomToast({ type: 'info', title: 'Unfollowed', message: `You unfollowed ${product.seller_name || 'this seller'}` });
      } else {
        await followAPI.followUser(product.seller_id);
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
        showCustomToast({ type: 'success', title: 'Following', message: `You are now following ${product.seller_name || 'this seller'}` });
      }
      // Sync live follower count from seller profile
      try {
        const res = await followAPI.getSellerProfile(product.seller_id);
        const count = res?.data?.total_followers ?? res?.data?.data?.total_followers;
        if (typeof count === 'number') setFollowerCount(count);
      } catch {}
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Error', message: err.message || 'Failed to update follow.' });
    } finally { setCheckingFollowStatus(false); }
  };

  const toggleFavorite = async () => {
    if (!localStorage.getItem('authToken')) {
      showCustomToast({ type: 'error', title: 'Login Required', message: 'Please log in to use wishlist.' });
      navigate('/auth'); return;
    }
    if (togglingFavorite) return;
    const pid = product._id || product.id;
    const name = product.title || product.name;
    try {
      setTogglingFavorite(true);
      if (isFavorite) {
        await wishlistAPI.removeFromWishlist(pid);
        setIsFavorite(false);
        showCustomToast({ type: 'info', title: 'Removed from Wishlist', message: `${name} removed.` });
      } else {
        await wishlistAPI.addToWishlist(pid);
        setIsFavorite(true);
        showCustomToast({ type: 'success', title: 'Added to Wishlist!', message: `${name} added.` });
      }
    } catch (err) {
      showCustomToast({ type: 'error', title: 'Error', message: err.message || 'Failed to update wishlist.' });
    } finally { setTogglingFavorite(false); }
  };

  /* ── Helpers ─────────────────────────────────────────────────── */
  const getProductImages = () => {
    if (!product) return [];
    if (product.images?.length > 0) return product.images.map(img => typeof img === 'string' ? img : img.url);
    return [product.image || iphoneImage];
  };

  // A live Flash Deal (Product.is_flash_deal — set from Admin -> Products ->
  // Flash Deal) replaces the retail price, same as the backend's own
  // EffectivePurchasePrice does at cart/checkout time — so the price shown
  // here always matches what the buyer is actually charged. Variant pricing
  // still wins when a variant is selected, since flash_deal_price is a single
  // figure for the base product and may not make sense for every variant.
  const isFlashDealActive = !!product?.is_flash_deal && product?.flash_deal_price > 0 &&
    (!product?.flash_deal_ends_at || new Date(product.flash_deal_ends_at) > new Date());
  const getCurrentPrice = () => selectedVariant?.price ?? (isFlashDealActive ? product.flash_deal_price : product?.price) ?? 0;
  const getCurrentStock = () =>
    selectedVariant?.stock !== undefined ? selectedVariant.stock : (product?.quantity ?? product?.stock ?? 0);
  const getAverageRating = () => product?.average_rating || 0;

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: product?.currency || 'NGN' }).format(amount || 0);

  const getDisplayName = (nameString) => {
    // Non-premium sellers: mask identity as "Seller XXX"
    if (!product?.seller_premium) {
      const sellerId = product?.seller_id;
      const idSuffix = sellerId ? String(sellerId).slice(-3).padStart(3, '0') : '000';
      return `Seller ${idSuffix}`;
    }
    if (!nameString) return 'AutoboyExpress Seller';
    const clean = String(nameString).trim().replace(/\s+/g, ' ');
    const words = clean.split(' ');
    if (words.length < 2) return clean;
    return words.filter((w, i) => i === 0 || w.toLowerCase() !== words[i - 1].toLowerCase()).join(' ');
  };


  /* ── Loading / Error guards ──────────────────────────────────── */
  if (loading) {
    return (
      <div className={`product-details pd-root${isDarkMode ? ' dark-mode' : ''}`}>
        <div className="pd-loading-state">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" style={{ color: '#22C55E' }} />
          <p>Loading product…</p>
        </div>
      </div>
    );
  }
  if (error || !product) {
    return (
      <div className={`product-details pd-root${isDarkMode ? ' dark-mode' : ''}`}>
        <div className="pd-error-state">
          <p>{error || 'Product not found'}</p>
          <button onClick={() => navigate('/shop')}>Back to Shop</button>
        </div>
      </div>
    );
  }

  const images = getProductImages();
  const displaySimilar = (similarProducts.length > 0 ? similarProducts : relatedProducts).slice(0, 4);

  /* ════════════════════════════════════════════════════════════════
     Render
  ═══════════════════════════════════════════════════════════════════ */
  return (
    <div
      className={`product-details pd-root${isDarkMode ? ' dark-mode' : ''}`}
      style={{ '--pd-dominant-rgb': dominantRGB }}
    >
      {/* Dynamic ambient background */}
      <div className="pd-hero-radial-bg" />

      <div className="pd-container">

        {/* Breadcrumb */}
        <nav className="pd-breadcrumb">
          <a href="/">Home</a>
          <span>/</span>
          <a href="/shop">Shop</a>
          <span>/</span>
          <a href={`/shop?category=${product.category_id}`}>{product.category || 'Products'}</a>
          <span>/</span>
          <span>{product.title || product.name}</span>
        </nav>

        {/* Hero grid: gallery (55%) + info pane (45%) */}
        <div className="pd-hero-grid">

          {/* Gallery — sticky */}
          <ProductGallery
            images={images}
            productName={product.title || product.name}
            selectedImage={selectedImage}
            onImageSelect={(i) => { setSelectedImage(i); setLightboxIndex(i); }}
            onOpenLightbox={(i) => { setLightboxIndex(i); setLightboxOpen(true); }}
            onDominantColor={setDominantRGB}
          />

          {/* Info pane */}
          <div className="pd-info-pane">
            <ProductActions
              product={product}
              selectedVariant={selectedVariant}
              onVariantChange={setSelectedVariant}
              isFavorite={isFavorite}
              togglingFavorite={togglingFavorite}
              onToggleFavorite={toggleFavorite}
              addingToCart={addingToCart}
              onAddToCart={handleAddToCart}
              hasPriceAlert={hasPriceAlert}
              settingPriceAlert={settingPriceAlert}
              onTogglePriceAlert={handleTogglePriceAlert}
              isSeller={isSeller}
              isReseller={isReseller}
              formatCurrency={formatCurrency}
              getCurrentPrice={getCurrentPrice}
              isFlashDealActive={isFlashDealActive}
              getCurrentStock={getCurrentStock}
              getAverageRating={getAverageRating}
            />

            <SellerInsight
              product={product}
              isFollowing={isFollowing}
              checkingFollowStatus={checkingFollowStatus}
              followerCount={followerCount}
              onToggleFollow={toggleFollow}
              getDisplayName={getDisplayName}
              navigate={navigate}
              isAuthenticated={isAuthenticated}
              user={user}
            />
          </div>
        </div>

        {/* Tabs section */}
        <div className="pd-tabs-section">
          <div className="pd-tabs-nav">
            {[
              { key: 'about', label: 'About' },
              { key: 'specs', label: 'Specifications' },
              { key: 'reviews', label: `Reviews${product.total_reviews > 0 ? ` (${product.total_reviews})` : ''}` },
              { key: 'qa', label: 'Q&A' },
            ].map(tab => (
              <button
                key={tab.key}
                className={`pd-tab-btn${activeTab === tab.key ? ' is-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="pd-tab-content">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={tabVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >

                {/* ── About ── */}
                {activeTab === 'about' && (
                  <div className="pd-tab-pane">
                    <div className="pd-features-grid">
                      <div className="pd-feature-col">
                        <h4>Product Information</h4>
                        <ul className="pd-feature-list">
                          {product.brand && <li><strong>Brand</strong> {product.brand}</li>}
                          {product.model && <li><strong>Model</strong> {product.model}</li>}
                          {product.color && <li><strong>Color</strong> {product.color}</li>}
                          {product.condition && <li><strong>Condition</strong> {product.condition.replace('_', ' ').toUpperCase()}</li>}
                          {product.sku && <li><strong>SKU</strong> {product.sku}</li>}
                          {product.weight && <li><strong>Weight</strong> {product.weight} kg</li>}
                        </ul>
                      </div>
                      <div className="pd-feature-col">
                        <h4>Description</h4>
                        <p className="pd-feature-desc">
                          {product.description || 'No description available for this product.'}
                        </p>
                      </div>
                      {product.specifications && Object.keys(product.specifications).length > 0 && (
                        <div className="pd-feature-col">
                          <h4>Specifications</h4>
                          <ul className="pd-feature-list">
                            {Object.entries(product.specifications).map(([k, v]) => (
                              <li key={k}>
                                <strong>{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong> {v}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Specs ── */}
                {activeTab === 'specs' && (
                  <div className="pd-tab-pane">
                    {product.specifications && Object.keys(product.specifications).length > 0 ? (
                      <div className="pd-features-grid">
                        <div className="pd-feature-col">
                          <h4>Product Specifications</h4>
                          <ul className="pd-feature-list pd-feature-list--specs">
                            {Object.entries(product.specifications).map(([k, v]) => (
                              <li key={k}>
                                <strong>{k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                                <span>{v}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <p className="pd-empty-text">No detailed specifications available.</p>
                    )}
                  </div>
                )}

                {/* ── Reviews ── */}
                {activeTab === 'reviews' && (
                  <div className="pd-tab-pane">
                    {ratingStats && (
                      <div className="pd-review-summary">
                        <div className="pd-rating-big">
                          <span className="pd-rating-number">{ratingStats.avgRating?.toFixed(1) || '0.0'}</span>
                          <span className="pd-rating-star">★</span>
                          <p>{ratingStats.totalReviews || 0} reviews</p>
                        </div>
                        <div className="pd-rating-bars">
                          {[5, 4, 3, 2, 1].map(stars => {
                            const count = ratingStats.distribution?.[stars] || 0;
                            const pct = ((count / (ratingStats.totalReviews || 1)) * 100).toFixed(0);
                            return (
                              <div key={stars} className="pd-rating-bar-row">
                                <span>{stars}</span>
                                <span className="pd-rating-bar-star">★</span>
                                <div className="pd-rating-bar-track">
                                  <div className="pd-rating-bar-fill" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="pd-rating-bar-count">({count})</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="pd-reviews-header">
                      <h4>Customer Reviews</h4>
                      {isAuthenticated && (
                        <button className="pd-review-write-btn" onClick={() => setShowReviewForm(!showReviewForm)}>
                          {showReviewForm ? 'Cancel' : 'Write a Review'}
                        </button>
                      )}
                    </div>

                    {showReviewForm && (
                      <div className="pd-review-form-wrap">
                        <ReviewForm
                          productId={id}
                          orderId={null}
                          eligibleOrders={eligibleOrders}
                          productName={product?.title}
                          onSuccess={() => {
                            setShowReviewForm(false);
                            setReviewsPage(1);
                            const refresh = async () => {
                              try {
                                const [rr, sr] = await Promise.all([
                                  reviewsAPI.getProductReviews(id, 1, 10),
                                  reviewsAPI.getProductRatingStats(id),
                                ]);
                                if (rr?.data) setReviews(rr.data.reviews || []);
                                if (sr?.data) setRatingStats(sr.data);
                              } catch {}
                            };
                            refresh();
                            showCustomToast({ type: 'success', title: 'Success', message: 'Review submitted!' });
                          }}
                          onCancel={() => setShowReviewForm(false)}
                        />
                      </div>
                    )}

                    {reviewsLoading ? (
                      <div className="pd-loading-inline">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                      </div>
                    ) : reviews.length > 0 ? (
                      <div className="pd-reviews-list">
                        {reviews.map(r => <ReviewCard key={r._id || r.id} review={r} />)}
                      </div>
                    ) : (
                      <p className="pd-empty-text">No reviews yet. Be the first!</p>
                    )}
                  </div>
                )}

                {/* ── Q&A ── */}
                {activeTab === 'qa' && (
                  <div className="pd-tab-pane">
                    <div className="pd-qa-header">
                      <h4>Questions &amp; Answers</h4>
                      {isAuthenticated && (
                        <button className="pd-review-write-btn" onClick={() => setShowQuestionForm(!showQuestionForm)}>
                          {showQuestionForm ? 'Cancel' : 'Ask a Question'}
                        </button>
                      )}
                    </div>

                    {showQuestionForm && (
                      <div className="pd-question-form">
                        <p>Your Question</p>
                        <textarea
                          value={newQuestion}
                          onChange={e => setNewQuestion(e.target.value)}
                          placeholder="Ask something about this product…"
                          rows={3}
                        />
                        <div className="pd-question-form__actions">
                          <button
                            className="pd-question-form__cancel"
                            onClick={() => { setShowQuestionForm(false); setNewQuestion(''); }}
                          >Cancel</button>
                          <button
                            className="pd-question-form__submit"
                            onClick={handleSubmitQuestion}
                            disabled={submittingQuestion || !newQuestion.trim()}
                          >
                            {submittingQuestion ? 'Submitting…' : 'Submit'}
                          </button>
                        </div>
                      </div>
                    )}

                    {questionsLoading ? (
                      <div className="pd-loading-inline">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#22C55E' }} />
                      </div>
                    ) : questions.length > 0 ? (
                      <div className="pd-qa-list">
                        {questions.map(q => (
                          <div key={q._id || q.id} className="pd-qa-card">
                            <div className="pd-qa-question">
                              <span className="pd-qa-icon">❓</span>
                              <div>
                                <p className="pd-qa-text">{q.question}</p>
                                <span className="pd-qa-meta">
                                  Asked by {q.user?.name || 'Anonymous'}&nbsp;·&nbsp;
                                  {q.created_at ? new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently'}
                                </span>
                              </div>
                            </div>
                            {q.answer ? (
                              <div className="pd-qa-answer">
                                <span className="pd-qa-icon">💬</span>
                                <div>
                                  <span className="pd-qa-answer-label">Seller Answer</span>
                                  <p className="pd-qa-answer-text">{q.answer}</p>
                                </div>
                              </div>
                            ) : (
                              <p className="pd-qa-pending">No answer yet. The seller will respond soon.</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="pd-empty-qa">
                        <FontAwesomeIcon icon={faQuestionCircle} size="3x" style={{ opacity: 0.25 }} />
                        <p>No questions yet. Be the first to ask!</p>
                      </div>
                    )}
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Similar products */}
        {displaySimilar.length > 0 && (
          <div className="pd-similar-section">
            <h2 className="pd-similar-title">You Might Also Like</h2>
            <div className="pd-similar-grid">
              {displaySimilar.map(item => {
                const iid = item._id || item.id;
                const img = item.images?.[0]?.url || item.images?.[0] || item.image || iphoneImage;
                const title = item.title || item.name;
                const price = item.price || 0;
                const rating = item.average_rating || 0;
                return (
                  <div key={iid} className="pd-similar-card" onClick={() => navigate(`/product/${iid}`)}>
                    <img src={img} alt={title} className="pd-similar-card__img" />
                    <div className="pd-similar-card__body">
                      <h3 className="pd-similar-card__title">{title}</h3>
                      {rating > 0 && (
                        <div className="pd-similar-card__rating">
                          <span>{'★'.repeat(Math.round(rating))}</span>
                          <span>({rating.toFixed(1)})</span>
                        </div>
                      )}
                      <p className="pd-similar-card__price">{formatCurrency(price)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* CTA banner */}
      <section className="pd-cta-banner">
        <div className="pd-cta-inner">
          <h2 className="pd-cta-heading">Shop Smarter.<br />Live Sharper.</h2>
          <p className="pd-cta-sub">AutoboyExpress's Got Your Tech</p>
          <a href="/shop" className="pd-cta-btn">
            <span>Shop Now</span>
            <FontAwesomeIcon icon={faArrowRight} />
          </a>
        </div>
      </section>


      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={images.map(src => ({ src }))}
        plugins={[Zoom]}
        zoom={{
          maxZoomPixelRatio: 3, zoomInMultiplier: 2,
          doubleTapDelay: 300, doubleClickDelay: 300,
          doubleClickMaxStops: 2, keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100, pinchZoomDistanceFactor: 100,
          scrollToZoom: true,
        }}
        on={{ view: ({ index }) => { setLightboxIndex(index); setSelectedImage(index); } }}
        controller={{ closeOnBackdropClick: true }}
        carousel={{ finite: false, preload: 2 }}
        render={{
          buttonPrev: images.length <= 1 ? () => null : undefined,
          buttonNext: images.length <= 1 ? () => null : undefined,
        }}
      />
    </div>
  );
};

export default ProductDetails;
